import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { removePin } from '../../utils/enterprisePinnedTeams';
import { useToast } from '../../contexts/ToastContext';
import EntSkeleton from '../../components/enterprise/EntSkeleton';
import EntEmptyState from '../../components/enterprise/EntEmptyState';
import EntHero from '../../components/enterprise/EntHero';
import EntModal from '../../components/enterprise/EntModal';
import EntConfirmDialog from '../../components/enterprise/EntConfirmDialog';
import EntPerson from '../../components/enterprise/EntPerson';
import { roleLabel } from '../../utils/companyRoles';

function formatDate(d) {
  return d ? new Date(d).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : '—';
}

function maxMetric(arr, key) {
  const vals = (arr || []).map((m) => Number(m[key]) || 0);
  return vals.length ? Math.max(1, ...vals) : 1;
}

export default function EnterpriseTeamDetailPage() {
  const { enterpriseId, teamId } = useParams();
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  const [teamDetail, setTeamDetail] = useState(null);
  const [teamAnalytics, setTeamAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [employeeModalUserId, setEmployeeModalUserId] = useState(null);
  const [employeeAnalytics, setEmployeeAnalytics] = useState(null);
  const [employeeLoading, setEmployeeLoading] = useState(false);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [settingsModalOpen, setSettingsModalOpen] = useState(false);
  const [enterpriseMembers, setEnterpriseMembers] = useState([]);
  const [addModalLoading, setAddModalLoading] = useState(false);
  const [addModalError, setAddModalError] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteSending, setInviteSending] = useState(false);
  const [removeConfirmUserId, setRemoveConfirmUserId] = useState(null);
  const [removeLoading, setRemoveLoading] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const fetchTeamDetail = useCallback(() => {
    if (!enterpriseId || !teamId) return;
    return Promise.all([
      api.get(`/api/enterprise/team/${teamId}`, { params: { enterpriseId } }),
      api.get(`/api/enterprise/team/${teamId}/member-analytics`, { params: { enterpriseId } }),
    ]).then(([detailRes, analyticsRes]) => {
      setTeamDetail(detailRes.data);
      setTeamAnalytics(analyticsRes.data);
    });
  }, [enterpriseId, teamId]);

  useEffect(() => {
    if (!enterpriseId || !teamId) {
      setLoading(false);
      setError('Invalid team');
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    Promise.all([
      api.get(`/api/enterprise/team/${teamId}`, { params: { enterpriseId } }),
      api.get(`/api/enterprise/team/${teamId}/member-analytics`, { params: { enterpriseId } }),
    ])
      .then(([detailRes, analyticsRes]) => {
        if (!cancelled) {
          setTeamDetail(detailRes.data);
          setTeamAnalytics(analyticsRes.data);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          if (err.response?.status === 404 && enterpriseId && teamId) {
            removePin(enterpriseId, teamId);
          }
          const msg = err.response?.status === 404
            ? 'Team not found or you don’t have access.'
            : (err.response?.data?.error || 'Failed to load team');
          setError(msg);
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [enterpriseId, teamId]);

  useEffect(() => {
    if (!enterpriseId || !employeeModalUserId) {
      setEmployeeAnalytics(null);
      return;
    }
    let cancelled = false;
    setEmployeeLoading(true);
    setEmployeeAnalytics(null);
    api.get(`/api/enterprise/user/${employeeModalUserId}/analytics`, { params: { enterpriseId } })
      .then((res) => { if (!cancelled) setEmployeeAnalytics(res.data); })
      .catch(() => { if (!cancelled) setEmployeeAnalytics(null); })
      .finally(() => { if (!cancelled) setEmployeeLoading(false); });
    return () => { cancelled = true; };
  }, [enterpriseId, employeeModalUserId]);

  useEffect(() => {
    if (!employeeModalUserId) return;
    const handleEscape = (e) => {
      if (e.key === 'Escape') setEmployeeModalUserId(null);
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [employeeModalUserId]);

  useEffect(() => {
    if (!addModalOpen || !enterpriseId || !teamDetail) return;
    setAddModalLoading(true);
    setAddModalError('');
    api.get('/api/enterprise/members', { params: { enterpriseId } })
      .then((res) => {
        const list = res.data.members || [];
        const inTeamIds = new Set((teamDetail.activeMembers || []).map((m) => String(m.userId)));
        setEnterpriseMembers(list.filter((m) => !inTeamIds.has(String(m.userId))));
      })
      .catch(() => setEnterpriseMembers([]))
      .finally(() => setAddModalLoading(false));
  }, [addModalOpen, enterpriseId, teamDetail]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key !== 'Escape') return;
      if (deleteConfirmOpen) setDeleteConfirmOpen(false);
      else if (removeConfirmUserId) setRemoveConfirmUserId(null);
      else if (settingsModalOpen) setSettingsModalOpen(false);
      else if (addModalOpen) setAddModalOpen(false);
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [deleteConfirmOpen, removeConfirmUserId, settingsModalOpen, addModalOpen]);

  const handleAddToTeam = (userId) => {
    setAddModalError('');
    api.post('/api/enterprise/team/add-user', { enterpriseId, teamId, userId })
      .then(() => {
        showSuccess('Added to team');
        fetchTeamDetail();
        setEnterpriseMembers((prev) => prev.filter((m) => String(m.userId) !== String(userId)));
      })
      .catch((err) => setAddModalError(err.response?.data?.error || 'Failed to add'));
  };

  const handleInviteByEmail = (e) => {
    e.preventDefault();
    const email = (inviteEmail || '').trim().toLowerCase();
    if (!email) return;
    setInviteSending(true);
    setAddModalError('');
    api.post('/api/enterprise/invite', { enterpriseId, email, teamId })
      .then(() => {
        showSuccess('Invite sent');
        setInviteEmail('');
        fetchTeamDetail();
      })
      .catch((err) => {
        setAddModalError(err.response?.data?.error || 'Failed to send invite');
      })
      .finally(() => setInviteSending(false));
  };

  const handleRemoveFromTeam = (userId) => {
    setRemoveLoading(true);
    api.delete(`/api/enterprise/team/${teamId}/member/${userId}`, { params: { enterpriseId } })
      .then(() => {
        showSuccess('Removed from team');
        setRemoveConfirmUserId(null);
        fetchTeamDetail();
      })
      .catch((err) => showError(err.response?.data?.error || 'Failed to remove'))
      .finally(() => setRemoveLoading(false));
  };

  const handleDeleteTeam = () => {
    if (!enterpriseId || !teamId) {
      showError('Missing enterprise or team context');
      return;
    }
    setDeleteLoading(true);
    api
      .delete(`/api/enterprise/team/${teamId}`, { params: { enterpriseId } })
      .then(() => {
        removePin(enterpriseId, teamId);
        showSuccess('Team deleted');
        navigate(`/enterprise-app/${enterpriseId}/teams`);
      })
      .catch((err) => {
        const msg =
          err.response?.data?.error ||
          (err.response?.status === 404 ? 'Team not found or access denied.' : null) ||
          (err.response?.status === 403 ? 'You do not have permission to delete this team.' : null) ||
          (err.response?.status === 400 ? 'Invalid request. Check enterprise context.' : null) ||
          err.message ||
          'Failed to delete team';
        showError(msg);
      })
      .finally(() => setDeleteLoading(false));
  };

  const members = teamAnalytics?.members || [];
  const totalContacts = members.reduce((s, m) => s + (Number(m.contacts) || 0), 0);
  const totalCardTaps = members.reduce((s, m) => s + (Number(m.cardTaps) || 0), 0);
  const totalMeetingInvites = members.reduce((s, m) => s + (Number(m.meetingInvites) || 0), 0);
  const avgActivityScore = members.length
    ? Math.round(members.reduce((s, m) => s + (Number(m.activityScore) || 0), 0) / members.length)
    : 0;

  if (loading) {
    return (
      <div className="ent-content ent-team-detail-page">
        <Link to={`/enterprise-app/${enterpriseId}/teams`} className="ent-team-detail-back">
          ← Back to teams
        </Link>
        <EntSkeleton variant="cards" count={6} />
        <EntSkeleton variant="block" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="ent-content">
        <div className="ent-team-detail-error-state">
          <div className="ent-flash ent-flash-error" role="alert">{error}</div>
          <Link to={`/enterprise-app/${enterpriseId}/teams`} className="ent-btn-primary">
            Back to teams
          </Link>
        </div>
      </div>
    );
  }

  if (!teamDetail) {
    return null;
  }

  const team = teamDetail.team || {};
  const activeMembers = teamDetail.activeMembers || [];
  const pendingInvites = teamDetail.pendingInvites || [];

  return (
    <div className="ent-content ent-team-detail-page">
      <Link to={`/enterprise-app/${enterpriseId}/teams`} className="ent-team-detail-back">
        ← Back to teams
      </Link>
      <EntHero
        title={team.name}
        lead={`${teamDetail.activeCount ?? 0} active · ${teamDetail.pendingCount ?? 0} pending · Created ${formatDate(team.createdAt)}`}
        actions={(
          <>
            <button type="button" className="ent-btn-primary" onClick={() => setAddModalOpen(true)}>
              Add people
            </button>
            <button type="button" className="ent-btn-secondary" onClick={() => setSettingsModalOpen(true)}>
              Settings
            </button>
          </>
        )}
      />

      <section className="ent-section">
        <h2 className="ent-section-title">Team summary</h2>
        <div className="ent-team-detail-summary-grid">
          <div className="ent-card ent-team-summary-card">
            <div className="ent-card-label">Active members</div>
            <div className="ent-card-value">{teamDetail.activeCount ?? 0}</div>
          </div>
          <div className="ent-card ent-team-summary-card">
            <div className="ent-card-label">Pending invites</div>
            <div className="ent-card-value">{teamDetail.pendingCount ?? 0}</div>
          </div>
          <div className="ent-card ent-team-summary-card">
            <div className="ent-card-label">Total contacts</div>
            <div className="ent-card-value">{totalContacts}</div>
          </div>
          <div className="ent-card ent-team-summary-card">
            <div className="ent-card-label">Total cards shared</div>
            <div className="ent-card-value">{totalCardTaps}</div>
          </div>
          <div className="ent-card ent-team-summary-card">
            <div className="ent-card-label">Meeting invites</div>
            <div className="ent-card-value">{totalMeetingInvites}</div>
          </div>
          <div className="ent-card ent-team-summary-card">
            <div className="ent-card-label">Avg activity score</div>
            <div className="ent-card-value">{avgActivityScore}</div>
          </div>
        </div>
      </section>

      <section className="ent-section">
        <h2 className="ent-section-title">Active members</h2>
        {activeMembers.length === 0 ? (
          <EntEmptyState
            title="No active members yet"
            lead="Add existing people, or invite by email, using Add people."
          />
        ) : (
          <>
          <div className="ent-table-wrap ent-hide-sm">
            <table className="ent-table">
              <thead>
                <tr>
                  <th>Person</th>
                  <th>Role</th>
                  <th>Joined</th>
                  <th>Activity score</th>
                  <th className="ent-actions-cell"></th>
                </tr>
              </thead>
              <tbody>
                {activeMembers.map((m, idx) => {
                  const stats = members.find((x) => String(x.userId) === String(m.userId));
                  const isRemoveConfirm = removeConfirmUserId === m.userId;
                  return (
                    <tr key={m.userId ? String(m.userId) : `m-${idx}`}>
                      <td><EntPerson name={m.name} email={m.email} /></td>
                      <td>{roleLabel(m)}</td>
                      <td>{formatDate(m.joinedAt)}</td>
                      <td>{stats ? stats.activityScore : '—'}</td>
                      <td className="ent-actions-cell">
                        <button
                          type="button"
                          className="ent-btn-analytics"
                          onClick={() => setEmployeeModalUserId(m.userId)}
                        >
                          Analytics
                        </button>
                        {!isRemoveConfirm ? (
                          <button
                            type="button"
                            className="ent-btn-remove"
                            onClick={() => setRemoveConfirmUserId(m.userId)}
                          >
                            Remove
                          </button>
                        ) : (
                          <span className="ent-inline-confirm">
                            <button type="button" className="ent-btn-confirm-sm ent-btn-danger-sm" onClick={() => handleRemoveFromTeam(m.userId)} disabled={removeLoading}>Yes</button>
                            <button type="button" className="ent-btn-confirm-sm" onClick={() => setRemoveConfirmUserId(null)} disabled={removeLoading}>No</button>
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="ent-stack">
            {activeMembers.map((m, idx) => {
              const stats = members.find((x) => String(x.userId) === String(m.userId));
              const isRemoveConfirm = removeConfirmUserId === m.userId;
              return (
                <div key={m.userId ? String(m.userId) : `sm-${idx}`} className="ent-stack-card ent-stack-card-static">
                  <EntPerson name={m.name} email={m.email} meta={roleLabel(m)} />
                  <div className="ent-stack-meta">
                    <span>Joined {formatDate(m.joinedAt)}</span>
                    <span>Score {stats ? stats.activityScore : '—'}</span>
                  </div>
                  <div className="ent-stack-actions">
                    <button type="button" className="ent-btn-analytics" onClick={() => setEmployeeModalUserId(m.userId)}>
                      Analytics
                    </button>
                    {!isRemoveConfirm ? (
                      <button type="button" className="ent-btn-secondary ent-btn-sm" onClick={() => setRemoveConfirmUserId(m.userId)}>
                        Remove
                      </button>
                    ) : (
                      <span className="ent-inline-confirm">
                        <button type="button" className="ent-btn-confirm-sm ent-btn-danger-sm" onClick={() => handleRemoveFromTeam(m.userId)} disabled={removeLoading}>Yes</button>
                        <button type="button" className="ent-btn-confirm-sm" onClick={() => setRemoveConfirmUserId(null)} disabled={removeLoading}>No</button>
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          </>
        )}
      </section>

      {pendingInvites.length > 0 && (
        <section className="ent-section">
          <h2 className="ent-section-title">Pending invites</h2>
          <div className="ent-table-wrap ent-hide-sm">
            <table className="ent-table">
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Invited</th>
                  <th>Expires</th>
                </tr>
              </thead>
              <tbody>
                {pendingInvites.map((i, idx) => (
                  <tr key={idx}>
                    <td>{i.email}</td>
                    <td>{roleLabel(i)}</td>
                    <td>{formatDate(i.createdAt)}</td>
                    <td>{formatDate(i.expiresAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="ent-stack">
            {pendingInvites.map((i, idx) => (
              <div key={`p-${idx}`} className="ent-stack-card ent-stack-card-static">
                <strong>{i.email}</strong>
                <div className="ent-stack-meta">
                  <span>{roleLabel(i)}</span>
                  <span>Expires {formatDate(i.expiresAt)}</span>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {members.length > 0 && (
        <section className="ent-section">
          <h2 className="ent-section-title">Team comparison</h2>
          <div className="ent-team-comparison ent-team-comparison-page">
            {['contacts', 'cardTaps', 'meetingInvites', 'activeDays', 'activityScore'].map((key) => {
              const label = { contacts: 'Contacts', cardTaps: 'Cards shared', meetingInvites: 'Meeting invites', activeDays: 'Active days (30d)', activityScore: 'Activity score' }[key];
              const max = maxMetric(members, key);
              return (
                <div key={key} className="ent-comparison-metric">
                  <div className="ent-comparison-label">{label}</div>
                  <div className="ent-comparison-bars">
                    {members.map((mem, idx) => {
                      const val = Number(mem[key]) || 0;
                      const pct = max > 0 ? Math.min(100, (val / max) * 100) : 0;
                      return (
                        <div key={mem.userId ? String(mem.userId) : `mem-${idx}`} className="ent-comparison-bar-row">
                          <span className="ent-comparison-bar-name">{mem.name || mem.email || '—'}</span>
                          <div className="ent-comparison-bar-wrap">
                            <div className="ent-comparison-bar-fill" style={{ width: `${pct}%` }} />
                          </div>
                          <span className="ent-comparison-bar-value">{val}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      <EntModal
        open={addModalOpen}
        title="Add people"
        onClose={() => setAddModalOpen(false)}
        size="md"
      >
        {addModalError && <p className="ent-form-error">{addModalError}</p>}
        <p className="ent-muted-text ent-modal-hint">Add existing company members who are not in this team.</p>
        {addModalLoading ? (
          <div className="ent-loading ent-loading-modal">Loading…</div>
        ) : enterpriseMembers.length === 0 ? (
          <p className="ent-muted-text">No one else to add. Everyone is already on this team.</p>
        ) : (
          <ul className="ent-add-list">
            {enterpriseMembers.map((m) => (
              <li key={m.userId}>
                <EntPerson name={m.name} email={m.email} />
                <button type="button" className="ent-btn-primary ent-btn-sm" onClick={() => handleAddToTeam(m.userId)}>Add</button>
              </li>
            ))}
          </ul>
        )}
        <div className="ent-invite-by-email">
          <h3 className="ent-modal-subtitle">Invite by email</h3>
          <p className="ent-muted-text ent-modal-hint">They join the company and this team as a Member.</p>
          <form onSubmit={handleInviteByEmail} className="ent-invite-form">
            <input
              type="email"
              inputMode="email"
              autoComplete="email"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              placeholder="email@company.com"
              className="ent-input"
              disabled={inviteSending}
            />
            <button type="submit" className="ent-btn-primary ent-btn-sm" disabled={inviteSending || !inviteEmail.trim()}>
              {inviteSending ? 'Sending…' : 'Send invite'}
            </button>
          </form>
        </div>
      </EntModal>

      <EntModal
        open={settingsModalOpen}
        title="Team settings"
        onClose={() => setSettingsModalOpen(false)}
        size="sm"
      >
        <p className="ent-muted-text">Remove people from this team with Remove next to each member above.</p>
        <div className="ent-danger-zone" style={{ marginTop: 20 }}>
          <h3 className="ent-modal-subtitle">Delete team</h3>
          <p className="ent-muted-text">Members stay in the company but leave this team. Pending invites will still join the company, not this team.</p>
          <button
            type="button"
            className="ent-btn-danger"
            onClick={() => {
              setSettingsModalOpen(false);
              setDeleteConfirmOpen(true);
            }}
          >
            Delete team
          </button>
        </div>
      </EntModal>

      <EntConfirmDialog
        open={deleteConfirmOpen}
        title="Delete team"
        message={<>Are you sure you want to delete <strong>{team.name}</strong>?</>}
        detail="Current members stay in the company and become unassigned. Pending invites will still allow people to join the company, but not this team."
        confirmLabel="Delete team"
        danger
        busy={deleteLoading}
        onConfirm={handleDeleteTeam}
        onClose={() => !deleteLoading && setDeleteConfirmOpen(false)}
      />

      <EntModal
        open={!!employeeModalUserId}
        title="Member activity"
        onClose={() => setEmployeeModalUserId(null)}
        size="sm"
      >
        {employeeLoading ? (
          <div className="ent-loading ent-loading-modal">Loading activity…</div>
        ) : employeeAnalytics ? (
          <>
            <div className="ent-employee-metrics-grid">
              <div className="ent-employee-metric">
                <span className="ent-employee-metric-label">Contacts added</span>
                <span className="ent-employee-metric-value">{employeeAnalytics.contacts_added ?? 0}</span>
              </div>
              <div className="ent-employee-metric">
                <span className="ent-employee-metric-label">Cards shared</span>
                <span className="ent-employee-metric-value">{employeeAnalytics.card_taps ?? 0}</span>
              </div>
              <div className="ent-employee-metric">
                <span className="ent-employee-metric-label">Meeting invites</span>
                <span className="ent-employee-metric-value">{employeeAnalytics.meeting_invites ?? 0}</span>
              </div>
              <div className="ent-employee-metric">
                <span className="ent-employee-metric-label">Active days (30d)</span>
                <span className="ent-employee-metric-value">{employeeAnalytics.active_days ?? 0}</span>
              </div>
              <div className="ent-employee-metric">
                <span className="ent-employee-metric-label">Profile views</span>
                <span className="ent-employee-metric-value">{employeeAnalytics.profile_views ?? 0}</span>
              </div>
            </div>
            {employeeAnalytics.best_time && (
              <p className="ent-employee-meta">Best time: {employeeAnalytics.best_time}</p>
            )}
          </>
        ) : (
          <p className="ent-muted-text ent-employee-no-data">No analytics available for this member.</p>
        )}
      </EntModal>
    </div>
  );
}
